import React from 'react';
import { inr } from '../utils/format';

// Counting the drawer the way it is actually counted: note by note. The total is
// derived from the counts, never typed, so the figure in the books is the one the
// cashier physically counted — and the breakdown is kept for the hand-over.
// ₹500 is the largest note in circulation — ₹2000 was withdrawn in 2023 and ₹1000
// demonetised in 2016, so neither belongs on a drawer count. ₹1 and ₹2 are coins.
export const DENOMS = [500, 200, 100, 50, 20, 10, 5, 2, 1];
// Two columns, split the way a drawer is: the big notes on the left, the small
// change on the right — never interleaved across the two.
const COLUMNS = [[500, 200, 100, 50, 20], [10, 5, 2, 1]];

export const countsTotal = (counts = {}) =>
  DENOMS.reduce((s, d) => s + d * (Number(counts[d]) || 0), 0);

// "₹500 × 4 · ₹100 × 12" — how a stored count reads back in a table or tooltip.
export const countsSummary = (counts) => (counts && typeof counts === 'object'
  ? DENOMS.filter((d) => Number(counts[d]) > 0).map((d) => `₹${d} × ${counts[d]}`).join(' · ')
  : '');

// Which denominations differ between two counts — the useful half of a tally,
// since "short 200" is a puzzle but "two 100s missing" is an answer.
export const countsDiff = (counted, expected) => DENOMS
  .map((d) => ({ d, delta: (Number(counted?.[d]) || 0) - (Number(expected?.[d]) || 0) }))
  .filter((x) => x.delta !== 0)
  .map(({ d, delta }) => `₹${d} × ${delta > 0 ? '+' : '−'}${Math.abs(delta)}`)
  .join(' · ');

const CashCount = ({ counts, onChange, label = 'Count the drawer', compact }) => {
  const set = (d, v) => {
    const n = Math.max(0, Math.floor(Number(v) || 0));
    onChange({ ...counts, [d]: v === '' ? '' : n });
  };
  const total = countsTotal(counts);
  const pieces = DENOMS.reduce((s, d) => s + (Number(counts[d]) || 0), 0);

  return (
    <div className="cash-count">
      <div className="cash-count-head">
        <span className="muted small">{label}</span>
        <span className="muted small">{pieces} note{pieces === 1 ? '' : 's'} / coin{pieces === 1 ? '' : 's'}</span>
      </div>
      <div className={`cash-count-grid${compact ? ' compact' : ''}`}>
        {COLUMNS.map((col, i) => (
          <div className="cash-col" key={i}>
            {col.map((d) => {
              const n = Number(counts[d]) || 0;
              return (
                <label key={d} className={`cash-denom${n > 0 ? ' has' : ''}`}>
                  <span className="cash-denom-face">₹{d}</span>
                  <input type="number" min="0" step="1" inputMode="numeric" value={counts[d] ?? ''}
                    onChange={(e) => set(d, e.target.value)} placeholder="0" />
                  <span className="cash-denom-amt">{n > 0 ? inr(d * n) : '—'}</span>
                </label>
              );
            })}
          </div>
        ))}
      </div>
      <div className="cash-count-total">
        <span>Counted</span>
        <strong>{inr(total)}</strong>
      </div>
    </div>
  );
};

export default CashCount;
